import styled, { css } from 'styled-components';

import { COLORS, SPACE, STATUS_COLORS } from '../../../../theme/tokens';
import { Text } from '../../../../components';
import { MAILBOX_TILE_MIN_HEIGHT } from '../MailboxTile/MailboxTile.styles';

/**
 * The two add-tiles (Figma OWS-Flow 46:877): the free one with a green border, the paid one with a
 * dashed border. Same 14px radius and 16/20 padding as a mailbox tile, and the SAME HEIGHT as a
 * mailbox tile (one constant, defined by the tile that owns the skeleton), so the first grid row
 * never shows two different card heights. `justify-content: space-between` pins the
 * copy to the top and the button to the bottom.
 */
const tileBase = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${SPACE[20]};
  box-sizing: border-box;
  min-height: ${MAILBOX_TILE_MIN_HEIGHT}px;
  padding: ${SPACE[16]} ${SPACE[20]};
  border-radius: 14px;
  min-width: 0;
`;

/** The free tile: a hairline in the positive green, the exact spec-sheet colour. */
export const FreeTile = styled.div`
  ${tileBase}
  border: 1px solid ${STATUS_COLORS.positive};
`;

/** The paid tile: a dashed hairline. */
export const PaidTile = styled.div`
  ${tileBase}
  border: 1px dashed ${COLORS.textSubtle};
`;

/** The copy block at the top of either tile. */
export const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[8]};
  min-width: 0;
`;

/** "Add free mail on {domain}" / "More mailboxes": 16px bold. `$muted` is the paid tile's title,
 *  which the spec sheet draws in grey so the price below it is the loudest thing. */
export const Title = styled.span<{ $muted?: boolean }>`
  font-size: 16px;
  font-weight: 700;
  line-height: 27px;
  color: ${(p) => (p.$muted ? COLORS.textMuted : COLORS.textStrong)};
  overflow-wrap: anywhere;
`;

/** The domain inside the free tile's title, in the same positive green as its border. */
export const Domain = styled.span`
  color: ${STATUS_COLORS.positive};
`;

/** The paid tile's price, large and bold: the load-bearing line of the redesign. */
export const Price = styled(Text)`
  font-size: 20px;
  font-weight: 700;
  line-height: 27px;
  color: ${COLORS.textStrong};
`;

/** Names the price line for tests without adding a box: the line lays out exactly as a bare Price. */
export const PriceSlot = styled.div`
  display: contents;
`;

/** Body copy: 13px, a 420px measure (never a width, so it wraps inside the tile at phone width). */
export const Body = styled(Text)`
  display: block;
  max-width: 420px;
  font-size: 13px;
  line-height: 20px;
  color: ${COLORS.textSubtle};
`;

/** The action row at the bottom of a tile. */
export const Actions = styled.div`
  display: flex;
  align-items: center;
`;
